<template>
  <div>
    <validation-observer ref="checkoutPayment" tag="form" @submit.prevent="validateCheckoutPayment">
      <checkout-layout class="checkout-payment">
        <checkout-payment-details v-model="userInfo"
                                  :cities="cities"
                                  :form-submitted="formSubmitted"
                                  :registrationAvailable="!!customerId"/>
        <BillDeliveryOptions v-if="hasBillDeliverySection"
                             v-model="billAddress"
                             :deliveryType="billDeliveryType"
                             :cities="cities"
                             :form-submitted="formSubmitted"
                             @bill-delivery="billDeliveryType = $event"/>
        <ShipmentOptions v-model="packageAddress"
                         :deliveryType="packageDeliveryType"
                         :cities="cities"
                         :form-submitted="formSubmitted"
                         @package-delivery-type="packageDeliveryType = $event"
                         @pos-id="posID = $event"/>
        <PaymentOptions :paymentAvailable="paymentAvailable" :isCardDisabled="disableCardPay"
                        @payment-option="paymentOption = $event"/>
      </checkout-layout>
      <div class="terms-of-use-section">
        <checkout-layout>
          <checkout-payment-terms v-model="termsAgree"/>
        </checkout-layout>
      </div>
    </validation-observer>
    <bank-form :form-data="paymentForm" ref="bankForm"/>
  </div>
</template>

<script>
import CheckoutLayout from '../checkout-layout'
import CheckoutPaymentDetails from './checkout-payment-details'
import cartComponentsProps from '../config/cartComponentsProps'
import BillDeliveryOptions from './bill-delivery-options'
import ShipmentOptions from './shipment-options'
import PaymentOptions from './payment-options'
import BankForm from '../bank-form'
import {
  fetchCustomer,
  customerContact,
  getCities,
  billingAddress,
  deliveryAddress,
  placeOrder,
  paymentOrder,
  validateCartAndGetData
} from '@/api/checkout'
import { registration } from '@/api/registration'
import { customEvent } from '@/services/customEventBus'
import { getCookie, deleteCookie } from '@/utils/cookieController'
import { lsSave, lsRemove } from '@/utils/localStorageController'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import CheckoutPaymentTerms from './checkout-payment-terms'
import checkoutDataLayer from '../mixins/checkoutDataLayer'

export default {
  name: 'checkout-payment',
  mixins: [errorMessageHandler, checkoutDataLayer],
  components: {
    CheckoutPaymentTerms,
    CheckoutLayout,
    CheckoutPaymentDetails,
    BillDeliveryOptions,
    ShipmentOptions,
    PaymentOptions,
    BankForm
  },
  props: { ...cartComponentsProps },

  data () {
    return {
      validatedCartData: {},
      cities: [],
      userInfo: {
        firstName: '',
        lastName: '',
        phone: '',
        jmbg: '',
        email: '',
        createAccount: true,
        password: '',
        confirmPassword: '',
        line1: '',
        line2: '',
        postalCode: '',
        town: '',
        remarks: ''
      },
      billAddress: {
        line1: '',
        line2: '',
        postalCode: '',
        town: '',
        remarks: ''
      },
      packageAddress: {
        line1: '',
        line2: '',
        postalCode: '',
        town: '',
        remarks: ''
      },
      customerId: '',
      hasBillDeliverySection: false,
      billDeliveryType: '',
      packageDeliveryType: '',
      posID: '',
      paymentAvailable: false,
      paymentOption: '',
      registrationStatus: '',
      paymentForm: {},
      formSubmitted: false,
      paymentByCardEnabled: true,
      termsAgree: false
    }
  },

  async mounted() {
    customEvent.$on('validate-step', () => this.validateCheckoutPayment());
    await this.getLocations();
    await this.fetchCustomerData();
    this.fetchCartDataValidate();
  },
  beforeDestroy () {
    customEvent.$off('validate-step')
  },

  computed: {
    disableCardPay () {
      return this.$props.cartData.tariffType === 'CABLE' || this.packageDeliveryType === 'ship-store' || !this.paymentByCardEnabled
    }
  },

  methods: {
    fetchCustomerData () {
      this.$loader.show()
      return fetchCustomer(this.$props.cartData.guid).then(({ data }) => {
        if (data.address) {
          Object.keys(this.userInfo).forEach(key => {
            this.userInfo[key] = data.address[key] ? data.address[key] : ''
            if (key === 'town') {
              this.userInfo[key] = data.address[key] ? data.address[key] : this.cities[0]
            }
          })
        }
        this.userInfo.createAccount = !!data.customerId // only if customer id is available offer registration
        this.customerId = data.customerId
        this.hasBillDeliverySection = data.address && !data.address?.firstName && !data.address?.lastName
        this.paymentAvailable = data.paymentAvailable
        this.paymentByCardEnabled = data.paymentByCardIsAvailable
      }).finally(() => {
        this.$loader.hide()
      })
    },
    getLocations () {
      return getCities().then(res => {
        if (res.data.cities) {
          this.cities = res.data.cities.map(city => {
            return city.name
          })
          this.userInfo.town = this.cities[0]
          this.billAddress.town = this.cities[0]
          this.packageAddress.town = this.cities[0]
        }
      })
    },
    fetchCartDataValidate () {
      const guid = getCookie('guid')
      if (guid) {
        validateCartAndGetData(guid).then(({ data }) => {
          this.validatedCartData = data.cart || {}
          this.checkoutDataLayer(this.validatedCartData.entries, this.$props.step, this.$props.userType)
        })
      }
    },
    async validateCheckoutPayment () {
      const { firstName, lastName, phone, jmbg, email, line1, line2, postalCode, town, remarks } = this.userInfo
      const dataToSend = {
        cartId: this.$props.cartData.guid,
        address: { firstName, lastName, phone, jmbg, email, line1, line2, postalCode, town, remarks }
      }

      const valid = await this.$refs.checkoutPayment.validate()
      this.formSubmitted = true
      if (!valid) {
        const errorEl = document.querySelector('.invalid')
        if (errorEl) {
          errorEl.scrollIntoView(false)
          errorEl.focus()
        }
      }
      if (valid) {
        this.$loader.show()
        customerContact(dataToSend).then(response => {
          if (this.hasBillDeliverySection) {
            const callStack = [this.setBillingAddress(response.data?.id), this.setPackageAddress(response.data?.id)]
            Promise.all(callStack).then(() => {
              this.postOrder(valid)
            }).catch(() => {
              this.$loader.hide()
              this.toast('error', {
                message: this.$t('errors.defaultError')
              })
            })
          } else {
            this.setPackageAddress(response.data?.id).then(() => {
              this.postOrder(valid)
            }).catch(() => {
              this.$loader.hide()
              this.toast('error', {
                message: this.$t('errors.defaultError')
              })
            })
          }
        }).catch(e => {
          this.handleErrorMessage(e.response.data)
          this.$loader.hide()
        })
      }
    },
    setBillingAddress (id) {
      const dataToSend = this.billingAddressType(id)
      return new Promise((resolve, reject) => {
        billingAddress(dataToSend).then(response => {
          resolve(response)
        }).catch(() => {
          reject(new Error(''))
        })
      })
    },
    billingAddressType (param) {
      let setParams
      if (this.billDeliveryType === 'bill-email') {
        setParams = { ebill: this.userInfo.email }
      } else if (this.billDeliveryType === 'bill-sameAddress') {
        setParams = {
          address: { id: param }
        }
      } else {
        const { line1, line2, postalCode, town, remarks } = this.billAddress
        setParams = {
          address: { line1, line2, postalCode, town, remarks }
        }
      }
      setParams.cartId = this.$props.cartData.guid
      return setParams
    },
    setPackageAddress (id) {
      const dataToSend = this.packageAddressType(id)
      return new Promise((resolve, reject) => {
        deliveryAddress(dataToSend).then(response => {
          resolve(response)
        }).catch(() => {
          reject(new Error(''))
        })
      })
    },
    packageAddressType (param) {
      let setParams
      if (this.packageDeliveryType === 'ship-existingAddress') {
        setParams = {
          address: { id: param }
        }
      } else if (this.packageDeliveryType === 'ship-differentAddress') {
        const { line1, line2, postalCode, town, remarks } = this.packageAddress
        setParams = {
          address: { line1, line2, postalCode, town, remarks }
        }
      } else {
        setParams = {
          address: { id: this.posID }
        }
      }
      setParams.cartId = this.$props.cartData.guid
      return setParams
    },
    postOrder (valid) {
      const dataToSend = {
        cartId: this.$props.cartData.guid
      }
      if (this.paymentOption) {
        dataToSend.paymentMode = this.paymentOption
      }

      placeOrder(dataToSend).then(response => {
        if (response.status >= 200 && response.status < 400) {
          if (this.userInfo.createAccount) {
            this.registerUser()
          }
          if (this.paymentOption === 'BY_CARD') {
            lsSave('ec_order_data', JSON.stringify(response.data))
            this.payByCard(response.data.code)
          } else {
            customEvent.$emit('validation-done', valid, 'success', response.data)
          }
          deleteCookie('guid')
        }
      }).catch(() => {
        customEvent.$emit('validation-done', valid, 'failed')
      }).finally(() => {
        this.$loader.hide()
      })
    },
    registerUser () {
      const dataToSend = {
        customerId: this.customerId,
        email: this.userInfo.email,
        newPassword: this.userInfo.password
      }
      registration(dataToSend)
    },
    payByCard (code) {
      paymentOrder(code).then(response => {
        this.paymentForm = response.data || {}
        lsSave('ec_show_order_confirmation', true)
        lsSave('ec_order_code', code)
        lsRemove('paymentType')

        setTimeout(() => {
          this.$refs.bankForm.submitForm()
        }, 50)
      }).catch(e => {
        this.handleErrorMessage(e.response.data)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.checkout-payment {
  margin-top: 3.2rem;
  margin-bottom: 4.5rem;

  @media (map_get($breakpoints, exsmmax)) {
    padding: 0 1.6rem;
  }

  @media (map_get($breakpoints, sm)) {
    margin-top: 4rem;
  }

  @media (map_get($breakpoints, lg)) {
    margin-bottom: 6.4rem;
  }
}

.terms-of-use-section {
  background-color: $white;
  margin-bottom: 4.5rem;

  .checkout-layout {
    @media (map_get($breakpoints, exsmmax)) {
      padding: 0 1.6rem;
    }
  }
}
</style>
